import XlsDownload from "@dana/Icons/XlsDownload";
import { elevation, themeColors } from "@dana/MobiusOverrides/styles/colors";
import Icon from "@insite/mobius/Icon";
import React, { forwardRef } from "react";
import styled from "styled-components";
import { InvoiceDetailsResponse } from "@dana/Types/ApiModels";
import { formatDateWithDash } from "@dana/Common/utils";
import translate from "@insite/client-framework/Translate";
import { EMEARegion, NARegion } from "@dana/Common/Constants";
import { formatDecimal } from "@dana/utils/utils";
import * as XLSX from "xlsx-js-style";

type NAInvoiceExcelDownloadButtonProps = {
    invoice?: InvoiceDetailsResponse | null;
    totalAmt?:number;    
    totalDiscount?:number;
    totalTariff?:number;
    totalCoreFee?:number;
    totalPrice?:number;
    fileName?: string;
    sheetName?: string;
    label?: string;
    size?: number;
    disabled?: boolean;
    className?: string;
    selectedRegion?:string;
    onBeforeDownload?: () => void | Promise<void>;
    onAfterDownload?: () => void;
};

const StyledExcelDownloadButton = styled.button`
    padding: 4px;
    background-color: transparent;
    border: none;
    cursor: pointer;

    &:hover {
        color: ${themeColors.secondary.danaDarkBlue};
    }

    &:focus-visible {
        color: ${themeColors.secondary.danaDarkBlue};
        border: 1px solid ${themeColors.primary.white};
        box-shadow: ${elevation.focus};
    }

    @media print {
        display: none;
    }
`;

const NUM_FMT = "0.00";

function applyAlignment(ws: XLSX.WorkSheet, cellAddress: string, horizontal: "right" | "left" | "center" = "right") {
    if (ws[cellAddress]) {
        ws[cellAddress].s = {
            ...(ws[cellAddress].s ?? {}),
            alignment: { horizontal },
        };
    }
}

function applyNumberFormat(ws: XLSX.WorkSheet, cellAddress: string) {
    if (ws[cellAddress]) {
        ws[cellAddress].z = NUM_FMT;
        applyAlignment(ws, cellAddress, "right");
    }
}

function buildInvoiceWorkbook(invoice: InvoiceDetailsResponse | null, 
    totalAmt:number,
    totalDiscount:number,
    totalTariff:number,
    totalCoreFee:number,
    totalPrice:number,
    fileName: string, sheetName: string, selectedRegion:string) {
    const rows: (string | number | null)[][] = [
        [translate("Customer"), invoice?.mailName ??""],
        [translate("Customer PO"), invoice?.poNumber ?? ""],
        [translate("Order Number"), invoice?.documentNumber ?? ""],
        [translate("Invoice Number"), invoice?.invoiceNumber ?? ""],
        [translate("Invoice Date"), formatDateWithDash(selectedRegion === EMEARegion ,invoice?.invoiceDate?.toString()?.split('T')[0] || "")],
        [translate("Payment Terms"), invoice?.termsDescription ??""],
        [],
        [],
        [
            translate("Dana Part Number"),
            translate("Customer Part Number"),
            translate("Part Description"),
            translate("Qty Ordered"),
            translate("Qty Shipped"),
            `${translate("Unit Price")} (${invoice?.currency ?? ""})`,
            `${translate("Unit Tariff Fee")} (${invoice?.currency ?? ""})`,
            `${translate("Unit Core Deposit")} (${invoice?.currency ?? ""})`,
            `${translate("Extended Price")} (${invoice?.currency ?? ""})`,
        ],
       ...(invoice?.invoiceInfo?.map(item => [
    item.partNumber,
    item.customerPartNumber,
    item.partDescription,
    item.quantityOrdered,
    item.quantityShipped,
    formatDecimal(item.pricePerUnit),
    formatDecimal(item.partPriceInfo?.find(p => p.priceType==="Tariff")?.amount ?? 0),
    formatDecimal(item.partPriceInfo?.find(p => p.priceType === "Core Fee")?.amount ?? 0),
    formatDecimal(
                                        (item.netAmount ?? 0) +
                                        (item.partPriceInfo?.find(p => p.priceType === "Tariff")?.amount ?? 0) *
                                            (item.quantityShipped ?? 0) +
                                        (item.partPriceInfo?.find(p => p.priceType === "Core Fee")?.amount ?? 0) *
                                            (item.quantityOrdered ?? 0)
                                    ),
]) ?? []),
        [],
        [null, null, null, null, null,null,null, `${translate("Item Total")} (${invoice?.currency ?? ""}):`, formatDecimal(totalPrice)],
        [null, null, null, null, null,null,null, `${translate("Tariff Fee Total")} (${invoice?.currency ?? ""}):`, formatDecimal(totalTariff ?? 0)],
        [null, null, null, null, null,null,null, `${translate("Core Deposit Total")} (${invoice?.currency ?? ""}):`, formatDecimal(totalCoreFee ?? 0)],
        [null, null, null, null, null,null,null, `${translate("Special Discount")} (${invoice?.currency ?? ""}):`, `-${formatDecimal(totalDiscount)}`],        
        [null, null, null, null, null,null,null, `${translate("Freight Charges")} (${invoice?.currency ?? ""}):`,formatDecimal(invoice?.freightAmount ?? 0)],
        [null, null, null, null, null,null,null, `${translate("Total")} (${invoice?.currency ?? ""}):`, formatDecimal(totalAmt)],
    ];

    const ws = XLSX.utils.aoa_to_sheet(rows);

    ws["!cols"] = [
        { wch: 18 },
        { wch: 26 },
        { wch: 16 },
        { wch: 12 },
        { wch: 16 },
        { wch: 16 },
        { wch: 16 },
        { wch: 28 },
        { wch: 16 },
    ];

    // Line item numeric columns: D (Qty), E (Unit Price), F (Cert Cost), G (Extended Price)
    const lineItemStartRow = 10; // row 10 = first data row (1-indexed, after 9 header rows)
    invoice?.invoiceInfo?.forEach((_, i) => {
        const rowNum = lineItemStartRow + i;
        applyNumberFormat(ws, `F${rowNum}`);
        applyNumberFormat(ws, `G${rowNum}`);
        applyNumberFormat(ws, `H${rowNum}`);
        applyNumberFormat(ws, `I${rowNum}`);
        
    });

    // // Totals block numeric column (G), 5 rows starting after the blank spacer row
    const totalsStartRow = lineItemStartRow + (invoice?.invoiceInfo?.length ?? 0) + 1;
    for (let i = 0; i < 6; i++) {
        applyNumberFormat(ws, `I${totalsStartRow + i}`);
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, `${fileName}.xlsx`);
}

export const NAInvoiceExcelDownloadButton = forwardRef<HTMLButtonElement, NAInvoiceExcelDownloadButtonProps>(
    (
        {
            invoice,
            totalAmt,
            totalTariff,
            totalCoreFee,
            totalDiscount,
            totalPrice,
            fileName = "invoice",
            sheetName = "Invoice",
            label = "Download Excel",
            size = 28,
            disabled,
            className,
            selectedRegion,
            onBeforeDownload,
            onAfterDownload,
        },
        ref,
    ) => {
        const handleDownload = async () => {
            if (disabled) return;
            if (typeof window === "undefined") return;

            try {
                await onBeforeDownload?.();
            } catch {
                // Swallow to avoid blocking download
            }

            try {
                buildInvoiceWorkbook(invoice ?? null,totalAmt ??0,totalDiscount??0,totalTariff??0,totalCoreFee??0,totalPrice??0, fileName, sheetName,selectedRegion ??"");
            } finally {
                onAfterDownload?.();
            }
        };

        return (
            <StyledExcelDownloadButton
                ref={ref}
                type="button"
                aria-label={label}
                title={label}
                onClick={handleDownload}
                className={className}
                disabled={disabled}
            >
                <Icon src={XlsDownload} size={size} />
            </StyledExcelDownloadButton>
        );
    },
);

NAInvoiceExcelDownloadButton.displayName = "NAInvoiceExcelDownloadButton";